// Plan 24b, Task 2.3 — Dice Roll Overlay (Screen 9)
//
// Shown when a player rolls dice. Two phases:
//   1. Roll Prompt phase (only for the local roller): "Tap to Roll!" / "Press A to Roll!"
//      with pulsing opacity. Click or Space/Enter fires payload.send({ type: 'roll_dice' }).
//   2. Result phase: subscribes to dice_roll event, mounts DiceResult component,
//      holds long enough to read, fades, then navigates back to 'board-hud'.
//
// DEFERRAL NOTE (Plan 24c): The current implementation treats overlay-end as a
// full router transition back to 'board-hud'. Plan 24c will introduce an
// "overlay-over-HUD" pattern where overlays render above the persistent HUD.

import { createDiceResult } from '../components/dice-result';
import type { ScreenHandle, ScreenMountContext } from './types';
import type { BoardMountPayload } from './board-payload';
import { deserializeGameState, type ServerMessage } from '@shared/protocol';
import { getInputModality } from '../../input/modality';
import { createBoardTopTimer } from './board-timer';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export type DiceRollOverlayPayload = BoardMountPayload & {
  /** The player who is rolling this turn. */
  readonly rollerPlayerId: string;
  /** Send the roll immediately after mount; used by the HUD roll button. */
  readonly autoRoll?: boolean;
  /** Optional already-known result when the overlay is mounted from a server event. */
  readonly initialResult?: number;
  /** Optional renderer-side dice spin. Resolves when the d20 settles. */
  playDiceRoll?: (rollerPlayerId: string, result: number) => Promise<DiceRollVisualHandle | void>;
  /** Called after the result burst has held and before returning to HUD. */
  onComplete?: (rollerPlayerId: string, result: number) => void;
  /** True when another system overlay should keep ownership of the visible screen. */
  shouldDeferBoardHudReturn?: () => boolean;
};

export interface DiceRollVisualHandle {
  readonly resultAnchor?: { readonly screenX: number; readonly screenY: number };
  readonly settled?: Promise<void>;
  fadeOut?(seconds: number): void;
  dispose(): void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Token constants — exact values from docs/design/tokens.json
// ─────────────────────────────────────────────────────────────────────────────

/** Keep the roll overlay transparent so the procedural die, not the screen, carries focus. */
const OVERLAY_BG = 'transparent';

/** animation.duration.idle-pulse = 2000ms → expressed in CSS seconds */
const IDLE_PULSE_DURATION = '2s';

/** color.text.secondary = #B0B8C8 */
const SECONDARY_TEXT_COLOR = '#B0B8C8';

/** typography.family.headline */
const HEADLINE_FAMILY = "'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif";

/** Result readability after reveal, followed by a short fade-out. */
const RESULT_HOLD_SECONDS = 1.35;
const RESULT_FADE_SECONDS = 0.25;
const RESULT_TOTAL_SECONDS = RESULT_HOLD_SECONDS + RESULT_FADE_SECONDS;
const ROLL_REJECTION_NOTIFICATIONS = new Set([
  'invalid_movement_direction',
  'nextNodeId required',
  'not_your_turn',
  'trapped',
]);

// ─────────────────────────────────────────────────────────────────────────────
// CSS for pulsing opacity animation (keyframe injection)
// ─────────────────────────────────────────────────────────────────────────────

const PULSE_STYLE_ID = 'dice-roll-pulse-style';

function ensurePulseStyle(): void {
  if (document.getElementById(PULSE_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = PULSE_STYLE_ID;
  style.textContent = `
    @keyframes dice-roll-prompt-pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.4; }
    }
  `;
  document.head.appendChild(style);
}

function getRollPromptText(): string {
  const modality = getInputModality();
  return modality === 'gamepad'
    ? 'Press A to Roll!'
    : modality === 'touch'
      ? 'Tap to Roll!'
      : 'Click or Press Enter to Roll!';
}

function isRollRejectedNotification(msg: ServerMessage): boolean {
  if (msg.type !== 'notification') return false;
  return ROLL_REJECTION_NOTIFICATIONS.has(msg.text) || msg.text.startsWith('wrong_phase:');
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createDiceRollOverlayScreen(
  ctx: ScreenMountContext<DiceRollOverlayPayload>,
): ScreenHandle {
  const { gsapRegistry, host, router, payload } = ctx;
  const { state: _state, localPlayerId, rollerPlayerId } = payload;

  let disposed = false;
  let rollSent = false;
  let resultStarted = false;
  let latestResult: number | null = null;

  // ── Root overlay ─────────────────────────────────────────────────────────

  const root = document.createElement('div');
  root.className = 'screen-dice-roll';
  root.setAttribute('data-screen-id', 'dice-roll');
  root.style.cssText = [
    'position: fixed',
    'inset: 0',
    `background: ${OVERLAY_BG}`,
    'display: flex',
    'flex-direction: column',
    'align-items: center',
    'justify-content: center',
    'z-index: 300',
  ].join('; ');

  const boardTimer = createBoardTopTimer(payload, { subscribe: false });
  root.appendChild(boardTimer.element);

  let pauseButton: HTMLButtonElement | null = null;
  const handlePauseClick = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    payload.onPause?.();
  };

  if (payload.onPause != null) {
    pauseButton = document.createElement('button');
    pauseButton.type = 'button';
    pauseButton.setAttribute('aria-label', 'Pause');
    pauseButton.setAttribute('data-action', 'pause');
    pauseButton.textContent = 'II';
    pauseButton.style.cssText = [
      'position: absolute',
      'top: 18px',
      'right: 18px',
      'width: 44px',
      'height: 44px',
      'border-radius: 8px',
      'border: 2px solid rgba(255,255,255,0.45)',
      'background: rgba(30,35,50,0.74)',
      'color: #FFFFFF',
      `font-family: ${HEADLINE_FAMILY}`,
      'font-size: 18px',
      'font-weight: 700',
      'line-height: 1',
      'cursor: pointer',
      'z-index: 1',
    ].join('; ');
    pauseButton.addEventListener('click', handlePauseClick);
    root.appendChild(pauseButton);
  }

  // ── Roll Prompt (only for local roller, before roll received) ────────────

  const isLocalRoller = rollerPlayerId === localPlayerId;

  let promptEl: HTMLElement | null = null;

  if (isLocalRoller) {
    ensurePulseStyle();

    promptEl = document.createElement('div');
    promptEl.setAttribute('data-dice-prompt', '');
    promptEl.style.cssText = [
      `font-family: ${HEADLINE_FAMILY}`,
      'font-size: 28px',
      'font-weight: 700',
      `color: ${SECONDARY_TEXT_COLOR}`,
      'text-align: center',
      'position: absolute',
      'bottom: 20%',
      'left: 0',
      'right: 0',
      'pointer-events: none',
      `animation: dice-roll-prompt-pulse ${IDLE_PULSE_DURATION} ease-in-out infinite`,
    ].join('; ');

    promptEl.textContent = getRollPromptText();

    root.appendChild(promptEl);
  }

  // ── Subscribe to server messages ─────────────────────────────────────────

  const unsubscribe = payload.subscribe((msg: ServerMessage) => {
    if (disposed) return;

    if (msg.type === 'state_sync') {
      boardTimer.updateFromGameState(deserializeGameState(msg.state));
      return;
    }

    if (
      msg.type === 'event' &&
      msg.event.type === 'dice_roll' &&
      msg.event.playerId === rollerPlayerId
    ) {
      void handleResult(msg.event.result);
      return;
    }

    if (isLocalRoller && rollSent && !resultStarted && isRollRejectedNotification(msg)) {
      rollSent = false;
      if (promptEl) {
        promptEl.textContent = getRollPromptText();
      }
    }
  });

  // ── Click / keyboard handler for local roller ────────────────────────────

  function sendRoll(): void {
    if (disposed || rollSent) return;
    rollSent = true;
    payload.playSfx?.('sfx-dice-roll');
    if (promptEl) {
      promptEl.textContent = 'Rolling...';
    }
    const nextNodeId = payload.getSelectedMovementNextNodeId?.() ?? null;
    payload.send(nextNodeId == null ? { type: 'roll_dice' } : { type: 'roll_dice', nextNodeId });
  }

  function handleKeydown(e: KeyboardEvent): void {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      sendRoll();
    }
  }

  if (isLocalRoller) {
    root.addEventListener('click', sendRoll);
    document.addEventListener('keydown', handleKeydown);
  }

  // ── Result phase ─────────────────────────────────────────────────────────

  let diceResultHandle: ReturnType<typeof createDiceResult> | null = null;
  let diceVisualHandle: DiceRollVisualHandle | null = null;
  let autoDisposeHandle: ReturnType<typeof gsapRegistry.create> | null = null;

  async function handleResult(value: number): Promise<void> {
    if (disposed || resultStarted) return;
    resultStarted = true;
    latestResult = value;

    // Hide the roll prompt once result arrives
    if (promptEl) {
      promptEl.style.display = 'none';
    }

    if (payload.playDiceRoll) {
      const visualHandle = (await payload.playDiceRoll(rollerPlayerId, value)) ?? null;
      if (disposed) {
        visualHandle?.dispose();
        return;
      }
      diceVisualHandle = visualHandle;
    }

    payload.playSfx?.('sfx-dice-land');

    // Mount the DiceResult component into the overlay
    if (!diceResultHandle) {
      diceResultHandle = createDiceResult({ gsapRegistry, mount: root });
    }

    // Trigger the reveal animation
    diceResultHandle.reveal(value, diceVisualHandle?.resultAnchor);

    // Register auto-dispose via gsapRegistry tl.call() so it is GSAP-tracked
    // and tests can inspect/invoke the callback directly.
    if (!autoDisposeHandle) {
      autoDisposeHandle = gsapRegistry.create('dice-roll:auto-dispose');
      autoDisposeHandle.timeline.to(
        root,
        {
          opacity: 0,
          duration: RESULT_FADE_SECONDS,
          ease: 'sine.in',
        },
        RESULT_HOLD_SECONDS,
      );
      autoDisposeHandle.timeline.call(
        () => {
          diceVisualHandle?.fadeOut?.(RESULT_FADE_SECONDS);
        },
        undefined,
        RESULT_HOLD_SECONDS,
      );
      autoDisposeHandle.timeline.call(
        () => {
          completeAfterVisualSettled();
        },
        undefined,
        RESULT_TOTAL_SECONDS,
      );
    }
  }

  function completeAfterVisualSettled(): void {
    const settled = diceVisualHandle?.settled;
    if (settled == null) {
      completeResultPhase();
      return;
    }
    void settled.then(completeResultPhase);
  }

  function completeResultPhase(): void {
    if (disposed) return;
    const result = latestResult;
    diceVisualHandle?.dispose();
    diceVisualHandle = null;
    if (payload.shouldDeferBoardHudReturn?.() === true) {
      dispose();
    } else {
      router.navigate('board-hud', payload);
    }
    if (result != null) {
      payload.onComplete?.(rollerPlayerId, result);
    }
  }

  // ── Mount to host ────────────────────────────────────────────────────────

  host.appendChild(root);

  if (isLocalRoller && payload.autoRoll === true) {
    queueMicrotask(sendRoll);
  } else if (payload.initialResult != null) {
    void handleResult(payload.initialResult);
  }

  // ── dispose ──────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    unsubscribe();

    if (isLocalRoller) {
      root.removeEventListener('click', sendRoll);
      document.removeEventListener('keydown', handleKeydown);
    }
    if (pauseButton != null) {
      pauseButton.removeEventListener('click', handlePauseClick);
    }
    boardTimer.dispose();

    if (autoDisposeHandle) {
      autoDisposeHandle.dispose();
      autoDisposeHandle = null;
    }

    if (diceResultHandle) {
      diceResultHandle.dispose();
      diceResultHandle = null;
    }

    if (diceVisualHandle) {
      diceVisualHandle.dispose();
      diceVisualHandle = null;
    }

    if (root.parentNode) {
      root.parentNode.removeChild(root);
    }
  }

  return { element: root, dispose };
}
